<template>
  <div class="search-widget" :class="{ 'search--open': open }">
    <div
      v-if="open"
      class="search-widget__backdrop"
      @click="toggleOpen(false)"></div>
    <div class="search__container">
      <template v-if="onMobile">
        <div class="search__open-trigger" @click="toggleOpen(false)">
          <div></div>
        </div>
        <div class="search__close-trigger" @click="toggleOpen(false)">
          <div></div>
        </div>
      </template>

      <!-- Search form. -->
      <form class="drupal__form" action="#" @submit.prevent="goSearch">
        <div class="form--inline clearfix">
          <div
            class="form-type-search-api-autocomplete"
            :class="{
              'form-type-search-api-autocomplete--active': showResults,
            }">
            <input
              :placeholder="t.placeholder"
              name="term"
              type="text"
              size="30"
              maxlength="128"
              autocomplete="off"
              ref="input"
              v-model="term"
              @click="toggleOpen(true)" />
          </div>
        </div>
      </form>

      <!-- Search suggestions. -->
      <div v-if="open && showResults" class="search-api-autocomplete-search">
        <div v-if="suggestions.length" class="search__autocomplete">
          <div class="search__autocomplete-items">
            <a
              v-for="item in suggestions"
              :key="item.id"
              :href="decodeURIComponent(item.link)"
              class="search__autocomplete-item"
              v-html="boldString(item.title, term)">
            </a>
          </div>
        </div>
        <div v-if="searchResults.length" class="search-widget__results">
          <div
            v-for="result in searchResults"
            :key="result.id"
            class="search-widget__results-item">
            <search-result :item="result" />
          </div>
        </div>
        <div class="search-widget__all-results" @click="goSearch">
          {{ t.allResults }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// components
import SearchResult from './SearchResult';
// utils
import scroll from '../../utils/scrollLock';
import { getSearchResults } from '../../utils/api';
import { isMobile } from '../../utils/isMobile';
import translationStrings from '../../translations/translationStrings';

export default {
  name: 'search-widget',
  components: { SearchResult },
  props: {
    lang: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      open: false,
      showResults: false,
      term: '',
      searchResults: [],
      relatedSearches: [],
      onMobile: isMobile(),
      t: translationStrings,
    };
  },
  watch: {
    open(value) {
      if (value) {
        if (this.onMobile) scroll.lock();
        this.getSuggestions();
      } else {
        if (this.onMobile) scroll.unlock();
        this.showResults = false;
      }
    },
    term(value) {
      if (!value || value === '' || (value && value.length <= 2)) {
        this.showResults = false;
      }

      if (value && value.length <= 2) return;

      if (this.open) {
        this.getSuggestions();
      }
    },
  },
  mounted() {
    // Get search term if present.
    const urlParams = new URLSearchParams(window.location.search);
    this.term = urlParams.get('search_api_fulltext');

    window.addEventListener('resize', this.resize);

    window.addEventListener('keyup', ({ key }) => {
      if (this.open && (key === 'Escape' || key === 'Esc')) {
        this.toggleOpen();
      }
    });
  },
  computed: {
    // Autocomplete suggestions.
    suggestions() {
      return this.relatedSearches.filter((item) => item.title !== '');
    },
  },
  methods: {
    async getSuggestions() {
      try {
        const { data } = await getSearchResults(
          this.lang,
          this.encode(this.term),
        );

        this.relatedSearches = data.related_searches;
        this.searchResults = data.results_data;
      } catch (error) {
        if (error.response) {
          console.error(error.response);
        }
      } finally {
        // only display results if there are any
        if (this.relatedSearches.length && this.searchResults.length) {
          this.showResults = true;
        }
      }
    },
    goSearch() {
      // Do not trigger search if term is undefined/null/empty string.
      if (this.term == null || this.term === '') return;
      const domain = window.location.origin;
      const redirectUrl = `${domain}${
        this.lang
      }search?search_api_fulltext=${this.encode(this.term)}`;
      window.location.replace(redirectUrl);
    },
    openWidget() {
      this.open = true;
      this.$refs.input.focus();
    },
    closeWidget() {
      this.open = false;
      this.$refs.input.value = '';
      this.$refs.input.blur();
    },
    toggleOpen(isInput) {
      if (isInput && this.onMobile) return;

      this.open ? this.closeWidget() : this.openWidget();
    },
    resize() {
      // re-check for mobile
      this.onMobile = isMobile();
      if (!this.onMobile) this.closeWidget();
    },
    encode(term) {
      if (term == null || term === '') return;
      return encodeURIComponent(String(term));
    },
    boldString(str, substr) {
      if (str == null || substr == null) return;

      const cleanSubstring = String(substr).replace(
        /[-[\]{}()*+!<=:?.\/\\^$|#\s,]/g,
        '\\$&',
      );
      const rgx = new RegExp(cleanSubstring, 'gi');
      if (typeof str === 'string') {
        return String(str)?.replaceAll(rgx, '<span>$&</span>');
      }
    },
  },
};
</script>
